import type {
	DcrhcoInput,
	DcrhcpInput,
	EntityType
} from '@/core/graphql/generated/types';
import {
	combinePhoneNumber,
	standardizePhoneNumberForSubmission
} from '@/utils/formatters/phoneNumberParts';

import {
	getCollection,
	getFieldEntries,
	toItemFieldId
} from '../fields/registry';
import type { CommonFieldDef, ItemFieldDef } from '../fields/types';

type Values = Record<string, unknown>;
type SerializedValue = string | number | boolean | string[] | Values[];

const nonEmptyString = (value: unknown) =>
	typeof value === 'string' && value.trim().length > 0
		? value.trim()
		: undefined;

export const serializePhone = (
	nationalNumber: unknown,
	countryIso?: unknown
): string | undefined => {
	const number = nonEmptyString(nationalNumber);
	if (!number) return undefined;

	const combined =
		typeof countryIso === 'string' && countryIso
			? combinePhoneNumber({ countryIso, nationalNumber: number })
			: number;

	return standardizePhoneNumberForSubmission(combined) || undefined;
};

export const serializeScalar = (
	def: Pick<CommonFieldDef, 'kind'>,
	value: unknown
): string | number | boolean | string[] | undefined => {
	switch (def.kind) {
		case 'year':
			return typeof value === 'number' && !isNaN(value) ? value : undefined;
		case 'boolean':
			return typeof value === 'boolean' ? value : undefined;
		case 'multiPicklist': {
			if (!Array.isArray(value)) return undefined;
			const ids = value
				.map(item =>
					typeof item === 'string' ? item : (item as { id?: string })?.id
				)
				.filter((id): id is string => !!id && id.trim().length > 0);
			return ids.length > 0 ? ids : undefined;
		}
		case 'phone':
			return serializePhone(value);
		default:
			return nonEmptyString(value);
	}
};

export const serializeItem = (
	fields: Readonly<Record<string, ItemFieldDef>>,
	item: Values,
	isUnavailable: (itemKey: string) => boolean = () => false
): Values | undefined => {
	const out: Values = {};
	let hasUserValue = false;

	Object.entries(fields).forEach(([key, def]) => {
		if (isUnavailable(key)) return;

		const value =
			def.kind === 'phone'
				? serializePhone(item[key], def.isoKey ? item[def.isoKey] : undefined)
				: serializeScalar(def, item[key]);

		if (value === undefined) return;

		out[key] = value;
		if (!def.defaultValue) hasUserValue = true;
	});

	return hasUserValue ? out : undefined;
};

export const toDcrInput = (
	entityType: EntityType,
	values: Values,
	unavailable: ReadonlySet<string> = new Set()
): DcrhcpInput | DcrhcoInput => {
	const out: Record<string, SerializedValue> = {};

	getFieldEntries(entityType).forEach(({ key, def }) => {
		if (unavailable.has(key)) return;

		if (def.kind === 'collection' && def.collection) {
			const items = Array.isArray(values[key]) ? (values[key] as Values[]) : [];
			const { fields } = getCollection(def.collection);
			const serialized = items
				.map(item =>
					serializeItem(fields, item, itemKey =>
						unavailable.has(toItemFieldId(key, itemKey))
					)
				)
				.filter((item): item is Values => !!item);

			if (serialized.length > 0) out[key] = serialized;
			return;
		}

		const value = serializeScalar(def, values[key]);
		if (value !== undefined) out[key] = value;
	});

	return out as DcrhcpInput | DcrhcoInput;
};
